import React, { useState, useCallback, useEffect } from "react";
import { authFetch, API_BASE } from "../api";
import "./CmsLoginModal.css";       // reuse modal chrome (overlay/box/header/buttons)
import "./DuplicateAppModal.css";   // small additions specific to this modal

/**
 * Modal: duplicate an existing app into a new one.
 *
 * Presents the source name, prompts for a new name (auto-seeded as
 * "<source>-copy"), and offers to carry forward secrets + vars. On
 * submit, POSTs to /api/integrations/:name/duplicate and calls
 * onCreated({ name, url }) so the parent can navigate into the new app.
 *
 * Visual chrome (overlay + box + form + button styling) is shared with
 * CmsLoginModal / ChangePasswordModal via the .cms-* classes — keeps
 * every modal in the app looking the same. Anything specific to this
 * flow (the "will be saved as" hint, the option-checkbox column) lives
 * in DuplicateAppModal.css.
 *
 * Structured errors from the backend (DUPLICATE_NAME_TAKEN,
 * DUPLICATE_SOURCE_MISSING, DUPLICATE_SAME_NAME) get mapped to
 * user-readable messages.
 */
export default function DuplicateAppModal({ source, onClose, onCreated }) {
  const [newName, setNewName] = useState(() => `${source.name}-copy`);
  const [includeSecrets, setIncludeSecrets] = useState(true);
  const [includeVars, setIncludeVars] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  // Mirror backend sanitizer so we give live feedback before submit.
  const sanitize = (name) =>
    (name || "")
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 30);

  const cleanName = sanitize(newName);
  const isSame = cleanName === source.name;
  const isEmpty = !cleanName;

  const submit = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isEmpty || isSame || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(
        `${API_BASE}/integrations/${encodeURIComponent(source.name)}/duplicate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ newName: cleanName, includeSecrets, includeVars }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body.code === "DUPLICATE_NAME_TAKEN") {
          throw new Error(`An app named "${cleanName}" already exists. Pick a different name.`);
        }
        if (body.code === "DUPLICATE_SOURCE_MISSING") {
          throw new Error(`The source app "${source.name}" is no longer available.`);
        }
        if (body.code === "DUPLICATE_SAME_NAME") {
          throw new Error("The new name must be different from the source.");
        }
        throw new Error(body.error || `Duplicate failed (HTTP ${res.status})`);
      }
      onCreated({ name: body.name, url: body.url });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }, [source.name, cleanName, isEmpty, isSame, busy, includeSecrets, includeVars, onCreated]);

  return (
    <div className="cms-overlay" onClick={busy ? undefined : onClose}>
      <div className="cms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cms-modal-header">
          <h2>Duplicate "{source.name}"</h2>
          <button className="cms-close" onClick={onClose} disabled={busy} aria-label="Close">&times;</button>
        </div>
        <p className="dup-description">
          Creates a new app with the same code. You can iterate on it without touching the original.
        </p>

        <form onSubmit={submit} className="cms-form">
          <label className="cms-label">
            <span>New app name</span>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={busy}
              autoFocus
              spellCheck={false}
            />
            <div className="dup-name-hint">
              Will be saved as <code>{cleanName || "<empty>"}</code>
              {isSame && <span className="dup-name-error">— must differ from source</span>}
            </div>
          </label>

          <div className="dup-options">
            <label className="dup-checkbox">
              <input
                type="checkbox"
                checked={includeSecrets}
                onChange={(e) => setIncludeSecrets(e.target.checked)}
                disabled={busy}
              />
              <span>Copy API keys <em>(otherwise you'll have to reconfigure them)</em></span>
            </label>
            <label className="dup-checkbox">
              <input
                type="checkbox"
                checked={includeVars}
                onChange={(e) => setIncludeVars(e.target.checked)}
                disabled={busy}
              />
              <span>Copy app variables</span>
            </label>
          </div>

          {error && <div className="cms-error">{error}</div>}

          <div className="dup-actions">
            <button type="button" className="dup-btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              className="cms-submit"
              disabled={busy || isEmpty || isSame}
              title={isEmpty ? "Enter a name" : isSame ? "Name must differ from source" : undefined}
            >
              {busy ? "Duplicating..." : "Create duplicate"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
